import React, { useRef, useEffect } from 'react';

interface TitleEditableFieldProps {
  initialValue: string;
  onChange: (value: string) => Promise<void>;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const TitleEditableField: React.FC<TitleEditableFieldProps> = ({
  initialValue,
  onChange,
  className,
  inputRef,
}) => {
  const [title, setTitle] = React.useState<string>(initialValue);
  const titleTextAreaRef = inputRef ?? useRef<HTMLTextAreaElement>(null);
  const singleLineHeight = className?.includes('text-xl') ? 28 : 40;

  const adjustTextareaHeight = () => {
    const textarea = titleTextAreaRef.current;
    if (!textarea) return;

    // Set to single line height, then expand only if content overflows
    textarea.style.height = `${singleLineHeight}px`;
    if (textarea.scrollHeight > textarea.clientHeight) {
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [title]);

  useEffect(() => {
    setTitle(initialValue);
  }, [initialValue]);

  return (
    <textarea
      ref={titleTextAreaRef}
      className={`box-border resize-none overflow-hidden p-1 font-semibold text-3xl w-full mb-4 focus-visible:outline-brand-taskonio-green border-0 font-['Figtree'] bg-transparent text-brand-grey-dark cursor-text ${className}`}
      style={{ minHeight: 0 }}
      value={title || ''}
      required={true}
      autoFocus={false}
      placeholder="Enter task name"
      onChange={(e) => {
        setTitle(e.target.value);
        adjustTextareaHeight();
      }}
      onBlur={async () => {
        const trimmedNewValue = title.trim();
        if (trimmedNewValue) {
          if (trimmedNewValue !== initialValue) {
            await onChange(trimmedNewValue);
          }
          setTitle(trimmedNewValue);
        } else {
          setTitle(initialValue);
        }
      }}
      onKeyDown={async (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          titleTextAreaRef.current?.blur();
        }
        if (e.key === 'Escape' || e.keyCode === 27) {
          e.stopPropagation();
          setTitle(initialValue);
        }
      }}
    />
  );
};
